import React, { useMemo, useCallback } from "react";
import { Box, Typography, Select, MenuItem, FormControl } from "@mui/material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  TooltipProps,
} from "recharts";
import {
  NameType,
  ValueType,
} from "recharts/types/component/DefaultTooltipContent";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { PRCycleTimeTrendData } from "../../../types/contractDashboard";
import styles from "./prCycleTimeTrend.module.scss";

interface PRCycleTimeTrendProps {
  data: PRCycleTimeTrendData[];
}

// Color constant
const LINE_COLOR = "#08ADC2";

// Custom tooltip component for better UX
const CustomTooltip: React.FC<TooltipProps<ValueType, NameType>> = ({
  active,
  payload,
  label,
}) => {
  if (!active || !payload || !payload.length) {
    return null;
  }

  const value = payload[0].value as number;
  const trend =
    payload[0].payload.previousValue !== undefined
      ? value - payload[0].payload.previousValue
      : null;

  return (
    <Box
      sx={{
        backgroundColor: "rgba(255, 255, 255, 0.96)",
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        padding: "12px",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
      }}
    >
      <Typography
        variant="body2"
        sx={{ fontWeight: 600, marginBottom: "8px", color: "#1a1a1a" }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          marginBottom: trend !== null ? "4px" : 0,
        }}
      >
        <Box
          sx={{
            width: "12px",
            height: "12px",
            borderRadius: "50%",
            backgroundColor: payload[0].color,
          }}
        />
        <Typography variant="body2" sx={{ color: "#666", fontSize: "13px" }}>
          Avg Cycle Time:
        </Typography>
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, color: "#1a1a1a", fontSize: "13px" }}
        >
          {value} hrs
        </Typography>
      </Box>
      {trend !== null && (
        <Box
          sx={{
            paddingTop: "4px",
            borderTop: "1px solid #f0f0f0",
            marginTop: "4px",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: trend < 0 ? "#10b981" : trend > 0 ? "#ef4444" : "#666",
              fontSize: "12px",
              fontWeight: 600,
            }}
          >
            {trend < 0 ? "↓" : trend > 0 ? "↑" : "→"}{" "}
            {Math.abs(trend).toFixed(1)} hrs from previous week
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const PRCycleTimeTrend: React.FC<PRCycleTimeTrendProps> = ({ data }) => {
  // Memoize chart data with previous value tracking for trend calculation
  const chartData = useMemo(() => {
    return data.map((item, index) => ({
      ...item,
      previousValue: index > 0 ? data[index - 1].avgCycleTime : undefined,
    }));
  }, [data]);

  // Memoize Y-axis domain calculation
  const yAxisDomain = useMemo(() => {
    if (!chartData || chartData.length === 0) return [0, "auto"];
    const values = chartData.map((d) => d.avgCycleTime);
    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);
    const padding = (maxValue - minValue) * 0.2;
    return [
      Math.max(0, Math.floor(minValue - padding)),
      Math.ceil(maxValue + padding),
    ];
  }, [chartData]);

  // Memoize Y-axis tick formatter
  const yAxisTickFormatter = useCallback((value: number) => {
    return `${value}h`;
  }, []);

  if (!data || data.length === 0) {
    return (
      <Box className={styles.container}>
        <Box className={styles.header}>
          <Box className={styles.titleSection}>
            <Typography variant="h6" className={styles.title}>
              PR Cycle Time Trend
            </Typography>
            <Typography variant="body2" className={styles.subtitle}>
              PR creation to PO release (hours).
            </Typography>
          </Box>
        </Box>
        <Box className={styles.chartPlaceholder}>
          <Typography variant="body2" className={styles.placeholderText}>
            No data available
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={styles.container}>
      <Box className={styles.header}>
        <Box className={styles.titleSection}>
          <Typography variant="h6" className={styles.title}>
            PR Cycle Time Trend
          </Typography>
          <Typography variant="body2" className={styles.subtitle}>
            PR creation to PO release (hours).
          </Typography>
        </Box>
        <FormControl className={styles.filter}>
          <Select
            defaultValue="Last 4 weeks"
            IconComponent={KeyboardArrowDownIcon}
            className={styles.select}
            aria-label="Time range filter"
          >
            <MenuItem value="Last 4 weeks">Last 4 weeks</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <Box className={styles.chartPlaceholder}>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart
            data={chartData}
            margin={{ top: 10, right: 30, left: 0, bottom: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey="week"
              tick={{ fontSize: 12, fill: "#6b7280" }}
              tickLine={{ stroke: "#e5e7eb" }}
              axisLine={{ stroke: "#e5e7eb" }}
              label={{
                value: "Avg Cycle Time",
                position: "insideBottom",
                offset: -10,
                style: { fontSize: 12, fill: LINE_COLOR, fontWeight: 600 },
              }}
            />
            <YAxis
              tickFormatter={yAxisTickFormatter}
              domain={yAxisDomain}
              tick={{ fontSize: 12, fill: "#6b7280" }}
              tickLine={{ stroke: "#e5e7eb" }}
              axisLine={{ stroke: "#e5e7eb" }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Line
              type="monotone"
              dataKey="avgCycleTime"
              stroke={LINE_COLOR}
              strokeWidth={3}
              name="Avg Cycle Time"
              animationDuration={800}
              dot={{
                fill: LINE_COLOR,
                strokeWidth: 2,
                r: 5,
                stroke: "#fff",
              }}
              activeDot={{
                r: 7,
                strokeWidth: 2,
                stroke: "#fff",
              }}
            />
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default React.memo(PRCycleTimeTrend);
